import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getApiLangParam } from "utils/helper";
import { ReactComponent as GlobeIcon } from "../../assets/globe.svg";
import { POPUP_STATUS } from "../../entities";
import Popup from "../Popup";
import styles from "./LanguageSwitcher.module.scss";

interface Language {
  code: string;
  label: string;
  name: string;
  nameTc: string;
  nameSc: string;
  ariaLabel: string;
}

interface LanguageSwitcherProps {
  isFormValuesDirty: boolean;
}

const LanguageSwitcher: FC<LanguageSwitcherProps> = ({ isFormValuesDirty }) => {
  const { i18n, t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const languages: Language[] = useMemo(
    () => [
      {
        code: "en",
        label: "EN",
        name: "English",
        nameTc: "英文",
        nameSc: "英文",
        ariaLabel: "Switch to English",
      },
      {
        code: "zh-HK",
        label: "繁",
        name: "Traditional Chinese",
        nameTc: "繁體中文",
        nameSc: "繁体中文",
        ariaLabel: "轉換至繁體中文",
      },
      {
        code: "zh-CN",
        label: "简",
        name: "Simplified Chinese",
        nameTc: "簡體中文",
        nameSc: "简体中文",
        ariaLabel: "转换至简体中文",
      },
    ],
    []
  );

  const pendingLanguageInfo = languages.find(
    (lang) => lang.code === pendingLanguage
  );

  const getLanguageName = (lang: Language) => {
    switch (i18n.language) {
      case "zh-HK":
        return lang.nameTc;
      case "zh-CN":
        return lang.nameSc;
      default:
        return lang.name;
    }
  };

  useEffect(() => {
    if (!pendingLanguage || !isChangingLanguage) return;

    const newSearchParams = new URLSearchParams(searchParams);
    const langQuery = getApiLangParam(pendingLanguage);
    newSearchParams.set("lang", langQuery);
    setSearchParams(newSearchParams);
    setShowConfirm(false);

    if (i18n.language !== pendingLanguage)
      setTimeout(() => window.location.reload(), 100);

    setIsChangingLanguage(false);
  }, [pendingLanguage, isChangingLanguage, searchParams, i18n.language]);

  const handleLanguageClick = useCallback(
    (lang: string) => {
      if (i18n.language === lang) return;

      // Disable confirmation popup for now
      if (isFormValuesDirty && false) {
        setPendingLanguage(lang);
        setShowConfirm(true);
      } else {
        setPendingLanguage(lang);
        setIsChangingLanguage(true);
      }
    },
    [i18n.language, isFormValuesDirty]
  );

  const confirmLanguageChange = () => setIsChangingLanguage(true);

  const cancelLanguageChange = () => {
    setShowConfirm(false);
    setPendingLanguage(null);
  };

  return (
    <div
      className={styles.languageSwitcher}
      role="region"
      aria-label="Language selector"
    >
      <GlobeIcon className={styles.globeIcon} />
      {languages.map((language) => (
        <button
          key={language.code}
          className={`${styles.languageButton} ${
            i18n.language === language.code ? styles.active : ""
          }`}
          onClick={() => handleLanguageClick(language.code)}
          aria-label={language.ariaLabel}
          aria-pressed={i18n.language === language.code}
          title={getLanguageName(language)}
        >
          {language.label}
        </button>
      ))}

      <Popup
        isOpen={showConfirm}
        onClose={cancelLanguageChange}
        onConfirm={confirmLanguageChange}
        title={
          <div>
            <div>{t("language.changeConfirmation.title")}</div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
              {i18n.language === "en"
                ? `轉換至${pendingLanguageInfo?.nameTc}？/ 转换至${pendingLanguageInfo?.nameSc}？`
                : i18n.language === "zh-HK"
                ? `Switch to ${pendingLanguageInfo?.name}？/ 转换至${pendingLanguageInfo?.nameSc}？`
                : `Switch to ${pendingLanguageInfo?.name}？/ 轉換至${pendingLanguageInfo?.nameTc}？`}
            </div>
          </div>
        }
        content={
          <div className={styles.confirmationContent}>
            <div className={styles.messagesContainer}>
              {i18n.language !== "en" && (
                <div className={styles.messageItem}>
                  <span className={styles.languageLabel}>EN</span>
                  <span>{t("language.changeConfirmation.messageEn")}</span>
                </div>
              )}
              {i18n.language !== "zh-HK" && (
                <div className={styles.messageItem}>
                  <span className={styles.languageLabel}>繁</span>
                  <span>{t("language.changeConfirmation.messageZhHk")}</span>
                </div>
              )}
              {i18n.language !== "zh-CN" && (
                <div className={styles.messageItem}>
                  <span className={styles.languageLabel}>简</span>
                  <span>{t("language.changeConfirmation.messageZhCn")}</span>
                </div>
              )}
            </div>
            <div className={styles.confirmQuestion}>
              {t("language.changeConfirmation.confirmQuestion")}
            </div>
          </div>
        }
        status={POPUP_STATUS.WARNING}
        closeTitle={t("common.cancel")}
        confirmTitle={
          <span style={{ whiteSpace: "nowrap" }}>
            {t("common.confirm").split("\n")}
          </span>
        }
        verticalButtons={false}
      />
    </div>
  );
};

export default LanguageSwitcher;
